/** The duplicates in flight, outside any page (plan O13): an activity's copy runs as a
 *  background task that can outlast the activity page, so the run is held here — a small
 *  route-independent store — and its end is announced by `DuplicateRunsHost`, mounted once in
 *  the app shell: a snackbar with **Open the copy** (or that nothing was created) wherever the
 *  operator is by then.
 *
 *  - One run per source activity (the server refuses a second start while one runs, 409
 *    `duplicate_in_progress`); a new start replaces a reported one.
 *  - The `DuplicatePopover` of the source activity follows its run when opened (again, after
 *    leaving and coming back too) and marks it `following` meanwhile: an end it shows is not
 *    announced again. Closed, the host announces it once. */
import { useSyncExternalStore } from 'react';

/** A duplicate the page follows: its task, the copy's name, and whether its end was shown. */
export interface DuplicateRun {
  taskId: string;
  name: string;
  /** The popover or the snackbar has told the operator how it ended. */
  reported: boolean;
}

/** A run the store holds: the page's `DuplicateRun`, its source, and who shows it. */
export interface HeldDuplicateRun extends DuplicateRun {
  /** The source activity. */
  activityId: number;
  /** The source activity by the name the app shows — the snackbar names it. */
  sourceTitle: string;
  /** The source's popover is open on it: the popover tells the end, not the host. */
  following: boolean;
}

let runs: ReadonlyMap<number, HeldDuplicateRun> = new Map();
let list: readonly HeldDuplicateRun[] = [];
const listeners = new Set<() => void>();

function commit(next: Map<number, HeldDuplicateRun>) {
  runs = next;
  list = [...next.values()];
  for (const l of listeners) l();
}

function update(activityId: number, change: (run: HeldDuplicateRun) => HeldDuplicateRun | null) {
  const run = runs.get(activityId);
  if (!run) return;
  const next = change(run);
  if (next === run) return;
  const map = new Map(runs);
  if (next) map.set(activityId, next);
  else map.delete(activityId);
  commit(map);
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A copy of `activityId` was queued (or a double start found the one running) — from its
 *  popover, which follows it while open (`following`). */
export function startDuplicateRun(
  activityId: number,
  run: { taskId: string; name: string; sourceTitle: string; following: boolean },
): void {
  const map = new Map(runs);
  map.set(activityId, { ...run, activityId, reported: false });
  commit(map);
}

/** The operator was told how the run `taskId` ended (by the popover or the snackbar). */
export function reportDuplicateRun(activityId: number, taskId: string): void {
  update(activityId, (r) => (r.taskId === taskId && !r.reported ? { ...r, reported: true } : r));
}

/** The source's popover is open (`true`) or closed on its run. */
export function setDuplicateFollowing(activityId: number, following: boolean): void {
  update(activityId, (r) => (r.following === following ? r : { ...r, following }));
}

/** Tests: forget every run. */
export function resetDuplicateRuns(): void {
  commit(new Map());
}

/** The run of `activityId`'s copy, while the store holds one. */
export function useDuplicateRun(activityId: number): HeldDuplicateRun | null {
  return useSyncExternalStore(subscribe, () => runs.get(activityId) ?? null);
}

/** Every run the store holds. */
export function useDuplicateRuns(): readonly HeldDuplicateRun[] {
  return useSyncExternalStore(subscribe, () => list);
}
